import type { EChartsOption } from 'echarts'
import type { DatasetOption } from 'echarts/types/dist/shared'

import type { ChartTheme } from '../chartTheme'
import { withLabelPlacement } from '../milestoneLabelPlacement'
import type { CashflowChartSource, ChartMilestone, ChartRun } from '../types'
import { buildBaseOption } from './base'
import { buildCashflowSeries, type RunVisibility } from './series'

export type CashflowChartOverlay = {
  baseline: CashflowChartSource
  /** Milestones for the baseline series (e.g. baseline retirement age). */
  baselineMilestones?: ChartMilestone[]
  /**
   * Which run, or runs, this chart draws. Defaults to the scenario.
   *
   * `both` draws the plan as it stands behind the scenario — faded on the
   * wealth lines, and as the line along the top of the old stack on the
   * cashflow bars, where a second set of columns would not fit.
   */
  run?: ChartRun
}

const BASELINE_SUFFIX = ' (baseline)'

const SCENARIO_LEGEND_NAMES = [
  'Income',
  'Taxes',
  'Expenses',
  'Contributions',
] as const
const BASELINE_LEGEND_NAMES = SCENARIO_LEGEND_NAMES.map(
  n => `${n}${BASELINE_SUFFIX}`
)

const stripBaselineSuffix = (name: string): string =>
  name.endsWith(BASELINE_SUFFIX) ? name.slice(0, -BASELINE_SUFFIX.length) : name

const forCashflowChart = (m: ChartMilestone): boolean => m.chart !== 'wealth'

export const buildCashflowChartOption = (
  source: CashflowChartSource,
  theme: ChartTheme,
  milestones: ChartMilestone[] = [],
  overlay?: CashflowChartOverlay
): EChartsOption => {
  const run = overlay?.run ?? 'scenario'
  const scenarioVisibility: RunVisibility =
    run === 'baseline' ? 'hidden' : 'lead'
  const baselineVisibility: RunVisibility =
    run === 'baseline' ? 'lead' : 'hidden'
  const ages = source.map(point => point.age)
  const scenarioSeries = buildCashflowSeries(
    theme,
    withLabelPlacement(milestones.filter(forCashflowChart), ages),
    scenarioVisibility,
    0,
    'scenario'
  )

  // Use a distinct stack id for the baseline bars so they stack together
  // separately from the scenario bars — otherwise visible+invisible bars
  // would share a stack column and the visible heights would be wrong.
  const baselineSeries = overlay
    ? buildCashflowSeries(
        theme,
        withLabelPlacement(
          (overlay.baselineMilestones ?? []).filter(forCashflowChart),
          overlay.baseline.map(point => point.age)
        ),
        baselineVisibility,
        1,
        'baseline',
        BASELINE_SUFFIX
      )
    : []

  const dataset: DatasetOption | DatasetOption[] = overlay
    ? [{ source }, { source: overlay.baseline }]
    : { source }

  const base = buildBaseOption(theme, 'Cashflow')

  const legend = overlay
    ? {
        ...base.legend,
        data:
          run !== 'baseline'
            ? [...SCENARIO_LEGEND_NAMES]
            : BASELINE_LEGEND_NAMES,
        formatter: stripBaselineSuffix,
      }
    : base.legend

  return {
    ...base,
    legend,
    series: [...scenarioSeries, ...baselineSeries],
    dataset,
  }
}
